"use client";

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { parseStrategyWeights } from "@/app/components/StrategyAboutContent";
import { weightLabelField } from "@/lib/methodology/example-fields";
import type { ExampleCompany, Spotlight } from "@/lib/methodology/example";
import type { MethodologyStrategy } from "@/lib/methodology/strategies";
import { RATING_TIER_GREEN, RATING_TIER_RED, RATING_TIER_YELLOW } from "@/lib/rating-tiers";
import { metricBarScoreColor } from "@/lib/stock-score-fields";
import "./MethodDiagram.css";

const METRICS = [
  "P/E",
  "EV/EBITDA",
  "Revenue growth",
  "EPS growth",
  "Dividend yield",
  "1Y return",
  "EBIT margin",
];
/** The chip that step 2 follows. */
const LINKED_METRIC = "Revenue growth";
const METRIC_COUNT = 70;

/**
 * Fallback when live data is unavailable: NVDA snapshot from `stocks_clean` (Sep 2026)
 * and the Pure Growth weights from `strategy_column_sets`.
 */
const FALLBACK_SPOTLIGHT: Spotlight = {
  label: "Revenue, Fwd 3Y, CAGR",
  field: "Exp3YRevenueGrowthCAGR",
  value: 60.6,
  percentile: 97.2,
  universe: 4139,
  bins: [],
  binMin: -20,
  binMax: 70,
};
const FALLBACK_ROWS: MetricRow[] = [
  { name: "Revenue, Hist 3Y, CAGR", weight: 10, score: 10 },
  { name: "Revenue, Fwd 1Y Growth", weight: 20, score: 10 },
  { name: "Revenue, Fwd 3Y, CAGR", weight: 30, score: 10 },
  { name: "Revenue, Fwd 5Y, CAGR", weight: 10, score: 10 },
  { name: "EPS Growth Year +1", weight: 10, score: 10 },
  { name: "EPS Growth Year +2", weight: 10, score: 10 },
  { name: "EPS Growth Year +3", weight: 10, score: 8 },
];
const FALLBACK_RATING = 99;

type MetricRow = { name: string; weight: number; score: number | null };

type MoveControls = {
  dragging: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

function ratingColor(value: number): string {
  if (value >= 67) return RATING_TIER_GREEN;
  if (value >= 34) return RATING_TIER_YELLOW;
  return RATING_TIER_RED;
}

function Arrow() {
  return (
    <div className="mth-arrow" aria-hidden>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/** Company counts per bin, square-root scaled so the long tail stays visible next to the peak. */
function Distribution({ spotlight }: { spotlight: Spotlight }) {
  const { bins, binMin, binMax, value } = spotlight;
  const x = Math.max(0, Math.min(100, ((value - binMin) / (binMax - binMin)) * 100));
  const peak = Math.sqrt(Math.max(1, ...bins));
  const width = 100 / Math.max(1, bins.length);
  return (
    <div className="mth-dist">
      {bins.length > 0 ? (
        <svg className="mth-dist-svg" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden>
          {bins.map((count, i) => {
            const h = count === 0 ? 0 : Math.max(1.5, (Math.sqrt(count) / peak) * 38);
            const below = (i + 1) * width <= x;
            return (
              <rect
                key={i}
                className={`mth-dist-bin ${below ? "is-below" : ""}`}
                x={i * width + 0.3}
                y={40 - h}
                width={width - 0.6}
                height={h}
                style={{ transitionDelay: `${150 + i * 14}ms` }}
              />
            );
          })}
        </svg>
      ) : null}
      <span className="mth-pin" style={{ "--mth-x": `${x}%` } as CSSProperties} />
    </div>
  );
}

export default function MethodDiagram({
  move,
  company,
  growth,
}: {
  move?: MoveControls;
  company?: ExampleCompany | null;
  growth?: MethodologyStrategy | null;
}) {
  const ref = useRef<HTMLElement>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setOn(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const spotlight = company?.spotlight ?? FALLBACK_SPOTLIGHT;
  const metricScore = Math.max(1, Math.round(spotlight.percentile / 10));

  const liveRows =
    company && growth
      ? parseStrategyWeights(growth.strategyweights).map(({ name, weight }) => {
          const field = weightLabelField(name);
          return { name, weight, score: field ? company.scores[field] ?? null : null };
        })
      : [];
  const rows = liveRows.length > 0 ? liveRows : FALLBACK_ROWS;
  const weighted = rows.reduce((sum, row) => sum + (row.weight * (row.score ?? 0)) / 100, 0);
  const liveRating = company?.ratings.PureGrowth;
  const rating = liveRating != null ? Math.round(liveRating) : FALLBACK_RATING;

  return (
    <figure ref={ref} className={`mth ${on ? "is-on" : ""} ${move?.dragging ? "is-dragging" : ""}`} aria-label="How a Bastion rating is built">
      {move ? (
        <div className="mth-move" onPointerDown={move.onPointerDown}>
          <span className="mth-grip" aria-hidden />
          <span className="mth-move-label">Drag to move</span>
          <span className="mth-move-spacer" />
          <button type="button" onClick={move.onMoveUp} disabled={!move.canMoveUp}>
            Up
          </button>
          <button type="button" onClick={move.onMoveDown} disabled={!move.canMoveDown}>
            Down
          </button>
        </div>
      ) : null}
      <div className="mth-grid">
        <section className="mth-step">
          <span className="mth-num">01</span>
          <h3 className="mth-title">Collect metrics</h3>
          <div className="mth-visual mth-chips">
            {METRICS.map((label, i) => (
              <span
                key={label}
                className={`mth-chip ${label === LINKED_METRIC ? "is-linked" : ""}`}
                style={{ transitionDelay: `${i * 45}ms` }}
              >
                {label}
              </span>
            ))}
            <span className="mth-chip mth-chip-more" style={{ transitionDelay: `${METRICS.length * 45}ms` }}>
              +{METRIC_COUNT - METRICS.length} more
            </span>
          </div>
          <p className="mth-caption">{METRIC_COUNT}+ fundamental and market metrics for every company.</p>
        </section>

        <Arrow />

        <section className="mth-step">
          <span className="mth-num">02</span>
          <h3 className="mth-title">Score each metric</h3>
          <div className="mth-visual mth-rank">
            <Distribution spotlight={spotlight} />
            <div className="mth-scale">
              {Array.from({ length: 10 }, (_, i) => (
                <span
                  key={i}
                  className={`mth-seg ${i + 1 === metricScore ? "is-hit" : ""}`}
                  style={i + 1 === metricScore ? ({ "--mth-hit": metricBarScoreColor(metricScore) } as CSSProperties) : undefined}
                >
                  {i + 1}
                </span>
              ))}
            </div>
          </div>
          <p className="mth-caption">Each value is ranked against all companies and becomes a score from 1 to 10.</p>
        </section>

        <Arrow />

        <section className="mth-step">
          <span className="mth-num">03</span>
          <h3 className="mth-title">Build the strategy rating</h3>
          <div className="mth-visual mth-combine">
            <div className="mth-bars">
              {rows.map((row, i) => (
                <span
                  key={row.name}
                  className={`mth-bar ${row.name === spotlight.label ? "is-linked" : ""}`}
                  style={{
                    flexGrow: row.weight,
                    height: `${(row.score ?? 0) * 10}%`,
                    transitionDelay: `${150 + i * 60}ms`,
                  }}
                />
              ))}
            </div>
            <div className="mth-legend">
              <span>width = weight</span>
              <span>height = score</span>
            </div>
            <div className="mth-result">
              <span className="mth-result-value">{weighted.toFixed(1)}</span>
              <svg className="mth-result-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="mth-result-value mth-rating" style={{ color: ratingColor(rating) }}>
                {rating}
              </span>
            </div>
          </div>
          <p className="mth-caption">Scores are weighted by strategy and combined into one rating from 1 to 100.</p>
        </section>
      </div>
    </figure>
  );
}
